import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Modal,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import axios from 'axios';
import { format } from 'date-fns';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminPatientList = () => {
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [subHospitals, setSubHospitals] = useState([]);
  const [filters, setFilters] = useState({
    doctor_id: '',
    status: '',
    visit_range: [],
    subhospital_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);

  const hospitalId = localStorage.getItem('hospitalId');
  const subHospitalId = localStorage.getItem('subHospitalId');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!hospitalId) return;
    axios
      .get(
        `${BASE_URL}/api/subHospitals/getSubHospitalsByHospital/${hospitalId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => setSubHospitals(res.data))
      .catch(() => setSubHospitals([]));
  }, [hospitalId, token]);

  useEffect(() => {
    if (!hospitalId || !filters.subhospital_id) {
      setDoctors([]);
      setFilters((f) => ({ ...f, doctor_id: '' }));
      return;
    }
    axios
      .get(`${BASE_URL}/api/doctor/getAllDoctors`, {
        params: {
          hospital_id: hospitalId,
          subhospital_id: filters.subhospital_id,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setDoctors(res.data))
      .catch(() => setDoctors([]));
    setFilters((f) => ({ ...f, doctor_id: '' }));
  }, [hospitalId, filters.subhospital_id, token]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const params = {
        hospital_id: hospitalId,
        subhospital_id: filters.subhospital_id || subHospitalId,
      };
      if (filters.doctor_id) params.doctor_id = filters.doctor_id;
      if (filters.status) params.status = filters.status;
      if (filters.visit_range.length === 2) {
        params.visit_start = format(filters.visit_range[0], 'yyyy-MM-dd');
        params.visit_end = format(filters.visit_range[1], 'yyyy-MM-dd');
      }
      const res = await axios.get(`${BASE_URL}/api/patient/admin/patients`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatients(res.data);
    } catch (err) {
      setPatients([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line
  }, [filters]);

  const showAppointmentHistory = async (patient) => {
    setSelectedPatient(patient);
    setHistoryModalVisible(true);
    setAppointmentHistory([]);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/patient/admin/patient/${patient.id}/appointments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppointmentHistory(res.data);
    } catch {
      setAppointmentHistory([]);
    }
  };

  const columns = [
    { label: 'Receipt No', key: 'receipt_no' },
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    { label: 'Age', key: 'age' },
    { label: 'Gender', key: 'gender' },
    { label: 'Total Visits', key: 'total_visits' },
    {
      label: 'Actions',
      key: 'actions',
      render: (patient) => (
        <Button
          variant="info"
          size="sm"
          className="global-btn"
          onClick={() => showAppointmentHistory(patient)}
        >
          View History
        </Button>
      ),
    },
  ];

  return (
    <div className="d-flex">
      <Container fluid className="py-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="global-heading mb-0">Patient Management</h2>
            <div className="text-muted mb-2 fw-semibold">
              View, filter, and manage all patients and their appointment history.
            </div>
          </Col>
        </Row>
        <Row className="mb-3 gx-2">
          <Col md={3} xs={12} className="mb-2 mb-md-0">
            <Form.Select
              value={filters.subhospital_id}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  subhospital_id: e.target.value || '',
                  doctor_id: '',
                }))
              }
            >
              <option value="">All Subhospitals</option>
              {subHospitals.map((sub) => (
                <option key={sub.sub_hospital_id} value={sub.sub_hospital_id}>
                  {sub.sub_hospital_name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3} xs={12} className="mb-2 mb-md-0">
            <Form.Select
              value={filters.doctor_id}
              onChange={(e) =>
                setFilters((f) => ({ ...f, doctor_id: e.target.value || '' }))
              }
              disabled={!filters.subhospital_id}
            >
              <option value="">All Doctors</option>
              {doctors.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2} xs={12} className="mb-2 mb-md-0">
            <Form.Select
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value || '' }))
              }
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="rescheduled">Rescheduled</option>
            </Form.Select>
          </Col>
          <Col md={3} xs={12} className="mb-2 mb-md-0">
            <InputGroup>
              <Form.Control
                type="date"
                value={filters.visit_range[0] ? format(filters.visit_range[0], 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const start = e.target.value ? new Date(e.target.value) : null;
                  setFilters((f) => ({
                    ...f,
                    visit_range: [start, f.visit_range[1] || null],
                  }));
                }}
              />
              <span className="input-group-text">to</span>
              <Form.Control
                type="date"
                value={filters.visit_range[1] ? format(filters.visit_range[1], 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const end = e.target.value ? new Date(e.target.value) : null;
                  setFilters((f) => ({
                    ...f,
                    visit_range: [f.visit_range[0] || null, end],
                  }));
                }}
              />
            </InputGroup>
          </Col>
          <Col md={1} xs={12}>
            <Button variant="primary" className="global-btn w-100" onClick={fetchPatients}>
              Refresh
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <div >
              {loading ? (
                <div className="text-center ">
                  <Spinner animation="border" variant="info" />
                </div>
              ) : (
                <Table  striped bordered hover className="AdminTable-root">
                  <thead >
                    <tr>
                      {columns.map((col) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {patients.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className="text-center text-muted">
                          No patients found.
                        </td>
                      </tr>
                    ) : (
                      patients.map((patient) => (
                        <tr key={patient.id}>
                          {columns.map((col) =>
                            col.render
                              ? <td key={col.key}>{col.render(patient)}</td>
                              : <td key={col.key}>{patient[col.key]}</td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </Col>
        </Row>
        <Modal
          show={historyModalVisible}
          centered
          size="lg"
          onHide={() => setHistoryModalVisible(false)}
          className="form-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedPatient
                ? `Appointment History: ${selectedPatient.name}`
                : 'Appointment History'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Table
              striped
              bordered
              hover
              size="sm"
              responsive
              className="AdminTable-root"
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Doctor</th>
                  <th>Specialty</th>
                  <th>Status</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {appointmentHistory.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center">
                      No appointment history found.
                    </td>
                  </tr>
                ) : (
                  appointmentHistory.map((app) => (
                    <tr key={app.id}>
                      <td>{app.appointment_date}</td>
                      <td>{app.start_time}</td>
                      <td>{app.end_time}</td>
                      <td>{app.doctor_name}</td>
                      <td>{app.specialties}</td>
                      <td>{app.status}</td>
                      <td>{app.reason}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default AdminPatientList;